import { type RefObject, useCallback } from 'react';
import type Webcam from 'react-webcam';

const VIDEO_READY_TIMEOUT_MS = 5000;

export const waitForVideoReady = (video: HTMLVideoElement, timeoutMs = VIDEO_READY_TIMEOUT_MS) => {
    if (video.readyState >= HTMLMediaElement.HAVE_ENOUGH_DATA) {
        return Promise.resolve(true);
    }

    return new Promise<boolean>((resolve) => {
        let settled = false;
        let timer: ReturnType<typeof setTimeout>;

        function finish(isReady: boolean) {
            if (settled) {
                return;
            }

            settled = true;
            clearTimeout(timer);
            video.removeEventListener('loadeddata', onReady);
            video.removeEventListener('canplay', onReady);
            resolve(isReady);
        }

        function onReady() {
            if (video.readyState >= HTMLMediaElement.HAVE_ENOUGH_DATA) {
                finish(true);
            }
        }

        timer = setTimeout(
            () => finish(video.readyState >= HTMLMediaElement.HAVE_ENOUGH_DATA),
            timeoutMs,
        );

        video.addEventListener('loadeddata', onReady);
        video.addEventListener('canplay', onReady);
    });
};

export const useResumeCameraStream = (webcamRef: RefObject<Webcam | null>) => {
    const resumeCameraStream = useCallback(async (): Promise<boolean> => {
        const video = webcamRef.current?.video;

        if (!video) {
            return false;
        }

        video.play().catch(() => {});

        const stream = video.srcObject as MediaStream | null;

        stream?.getVideoTracks().forEach((track) => {
            if (track.readyState === 'live') {
                track.enabled = true;
            }
        });

        return waitForVideoReady(video);
    }, [webcamRef]);

    return { resumeCameraStream };
};
